// Content sourced from ffyonaduncantherapies.co.uk + Ffyona's public professional profiles.
// Factual info (bio, qualifications, pricing, contact) preserved exactly.

export const CONTACT = {
  name: "Ffyona Duncan",
  business: "Ffyona Duncan Therapies",
  tagline: "By your side, piece by piece.",
  email: "ffyonaa@hotmail.com",
  phone: "07368 630982",
  phoneHref: "tel:+447368630982",
  location: "Swindon, Wiltshire",
  serviceModes: "In person, home visits & online (Zoom / phone)",
  hours: [
    { day: "Monday – Friday", time: "Flexible daytime & evening appointments" },
    { day: "Saturday", time: "Selected appointments available" },
    { day: "Sunday", time: "Closed" },
  ],
};

export const NAV = [
  { label: "Home", id: "home" },
  { label: "About", id: "about" },
  { label: "Counselling", id: "counselling" },
  { label: "Supervision", id: "supervision" },
  { label: "Reviews", id: "reviews" },
  { label: "FAQs", id: "faqs" },
  { label: "Contact", id: "contact" },
];

export const TRUST_ITEMS = [
  "Qualified Level 5 Counsellor",
  "Clinical Supervisor",
  "Free 30-minute consultation",
  "In-person & online sessions",
  "Confidential & non-judgemental",
  "MNCPS Accredited",
];

export const MARQUEE_WORDS = [
  "Anxiety", "Bereavement", "Stress", "Relationships", "Low confidence",
  "Life transitions", "Workplace stress", "Self-esteem", "Depression",
];

export const WHO_I_HELP = [
  {
    title: "Anxiety",
    icon: "Waves",
    text: "When worry feels constant and the mind won't settle, we'll gently make sense of it together and find calmer ground.",
  },
  {
    title: "Bereavement",
    icon: "Feather",
    text: "Grief has no timeline. With a foundation in bereavement care, I offer a tender space to carry loss and remember.",
  },
  {
    title: "Stress",
    icon: "CloudRain",
    text: "When everything feels like too much, we'll slow things down and rebuild a sense of steadiness, one step at a time.",
  },
  {
    title: "Relationship challenges",
    icon: "Heart",
    text: "Whether it's a partner, family or friendships, we'll explore the patterns and help you feel more understood.",
  },
  {
    title: "Low confidence",
    icon: "Sprout",
    text: "Together we'll quiet the harsh inner voice and reconnect you with your own worth and quiet strength.",
  },
  {
    title: "Life transitions",
    icon: "Compass",
    text: "Change — even good change — can unsettle us. I'll walk alongside you as you find your footing again.",
  },
  {
    title: "Workplace stress",
    icon: "Briefcase",
    text: "Burnout, pressure and difficult dynamics at work are real. We'll create space for you to breathe and reflect.",
  },
  {
    title: "Self-esteem",
    icon: "Sun",
    text: "You deserve to feel at home in yourself. We'll nurture a kinder, more accepting relationship with who you are.",
  },
];

export const HOW_IT_HELPS = [
  {
    n: "01",
    title: "A space that's entirely yours",
    text: "When you come to me, that hour belongs completely to you. There's no fixing, no judgement and no rushing — just a warm, confidential space where you can finally say the things you've been carrying.",
  },
  {
    n: "02",
    title: "Feeling truly heard",
    text: "So much of the healing begins simply with being understood. I won't reach for labels or diagnoses — instead we'll make sense of what you're feeling together, through honest, human conversation, always at your pace.",
  },
  {
    n: "03",
    title: "Gentle, lasting change",
    text: "Over our time together, the patterns that keep you stuck begin to soften. You start to feel lighter and more like yourself again — carrying tools and insights that stay with you long after our work ends.",
  },
];

export const BIO = {
  intro: "Thank you for taking a look at my website and the work that I do. My interest in counselling stems from undertaking this therapy in the past on a personal level, plus working in the care sector for 8 years, enabling me to work with presentations of bereavement and depression.",
  paragraphs: [
    "I qualified as a Level 5 Counsellor in October 2021. As part of my qualification I worked with a local charity, completing 100 hours in a voluntary counselling role.",
    "From 2021 to 2026 I worked for a college in Bristol and a college in Devon, lecturing part-time in Counselling Skills and Theory Level 3 and Therapeutic Counselling Level 4. Previously I have undertaken moderation for an online L2 course and I marked Level 2 exams for the examinations board. In early 2023 my colleague and I wrote a Level 1 Active Listening Course that was piloted over the 2023/24 academic year and is now active.",
    "In 2023 I spent 6 months mentoring a newly qualified lecturer through the Level 2 Counselling course.",
    "In October 2024 I qualified as a supervisor, enabling me to supervise both student and qualified counsellors in their work with clients. At the same time as studying my supervision qualification I also undertook a course in working online and via phone, in order to increase my capabilities.",
    "As part of my private practice I offer a wide range of models including person-centred, psychodynamic, Gestalt and Cognitive Behavioural Therapy with goal-focused solution work. I have a foundation certificate in Bereavement Care gained through Cruse. I am constantly expanding my practice with new techniques and packages.",
    "I offer supervision through my private practice. In the past I have worked for a local charity where I was employed as a clinical supervisor. I completed my 20 hours training with this charity and worked with a range of supervisees, both students and qualified.",
    "Today I am working as a lecturer in Health and Social Sciences, combining my teaching expertise with extensive knowledge of the care and mental health sector. I am also continuing to run my private practice for clients and supervisees.",
  ],
  whyIBecame: "My own experience of therapy showed me just how much it means to sit with someone who truly listens. That — and the years I've spent caring for others — is why I do this work.",
  qualifications: [
    "Level 5 Counsellor (2021)",
    "Qualified Supervisor (2024)",
    "Foundation Certificate in Bereavement Care (Cruse)",
    "Trained in online & telephone working",
    "Lecturer in Counselling & Health and Social Sciences",
    "MNCPS Accredited Member",
  ],
  values: ["Warmth", "Confidentiality", "Non-judgement", "Being led by you"],
};

export const SERVICES = {
  counselling: {
    id: "counselling",
    label: "Counselling",
    tone: "sage",
    tagline: "A safe, compassionate space to be heard.",
    description:
      "One-to-one counselling for adults navigating anxiety, grief, stress, relationship difficulties, low confidence and life's transitions. We work at your pace, in a way that feels right for you.",
    idealClient:
      "For anyone who feels they're struggling and would value a warm, confidential space to talk — whether you've had counselling before or this is your very first step.",
    included: [
      "Warm, non-judgemental one-to-one sessions",
      "An integrative approach tailored to you",
      "In person in Swindon, by Zoom or by phone",
      "Goal-focused solution work if you'd like it",
      "Complete confidentiality",
    ],
    session: "60-minute sessions · in person, online or by phone",
    prices: [
      { name: "Free 30-minute consultation", price: "Free", note: "A relaxed chat to explore your questions" },
      { name: "Initial consultation", price: "£45", note: "Includes questionnaire, goal setting & contracting" },
      { name: "Counselling session (1 hour)", price: "£50", note: "Ongoing sessions" },
    ],
  },
  supervision: {
    id: "supervision",
    label: "Supervision",
    tone: "beige",
    tagline: "Reflective support for your practice.",
    description:
      "Clinical supervision for qualified and student counsellors — a supportive space to explore client work, gain fresh insight and care for yourself as a practitioner.",
    idealClient:
      "For qualified counsellors seeking a reflective supervisory relationship, and for students needing supervision hours as part of their training.",
    included: [
      "A supportive, reflective supervisory relationship",
      "Space to explore client cases & ethical practice",
      "Support for both qualified & student counsellors",
      "Available online or by phone",
      "Rooted in ongoing CPD and best practice",
    ],
    session: "60 or 90-minute sessions · online or by phone",
    prices: [
      { name: "Supervision (60 mins)", price: "£50", note: "Qualified counsellors" },
      { name: "Supervision (90 mins)", price: "£65", note: "Qualified counsellors" },
      { name: "Student supervision (60 mins)", price: "£40", note: "Counsellors in training" },
      { name: "Student supervision (90 mins)", price: "£50", note: "Counsellors in training" },
    ],
  },
};

export const STEPS = [
  {
    n: "01",
    title: "Get in touch",
    text: "Send a short message or call. There's no pressure and no obligation — just a first, gentle step.",
  },
  {
    n: "02",
    title: "Free 30-minute consultation",
    text: "We'll have a relaxed chat about what's bringing you here and how counselling might help. A chance to ask anything.",
  },
  {
    n: "03",
    title: "Your first session",
    text: "We begin properly, in a warm and confidential space. You share only as much as you feel ready to.",
  },
  {
    n: "04",
    title: "Ongoing support, at your pace",
    text: "We continue for as long as feels right for you — reviewing together as we go. You're always in control.",
  },
];

// Real client reviews sourced from ffyonaduncantherapies.co.uk/reviews (verbatim).
export const REVIEWS = [
  {
    quote:
      "I found Ffyona's counselling incredibly helpful in navigating some difficult and tricky situations. She provided thoughtful advice and practical suggestions that I was able to apply in my everyday life. Ffyona was very empathetic and created a safe, non-judgmental space where I felt comfortable opening up about anything. Since completing therapy with her, I have felt more confident and better equipped to handle challenges. I highly recommend Ffyona to anyone looking for guidance and support.",
    author: "L.C.",
    location: "Verified client",
  },
  {
    quote:
      "Exactly what one would want from a counsellor. Ffyona is caring, compassionate, and guides and challenges oneself in a professional manner. I couldn't have asked for a better counsellor and I'm a better person for it.",
    author: "G.M.",
    location: "Verified client",
  },
  {
    quote:
      "Ffyona is a compassionate therapist who listens attentively and asks the right questions to get to the bottom of the issue. She has helped me so much in setting much-needed boundaries with straight-forward advice that's easy to apply. The more time goes by since finishing my sessions, the more I realise how far I've come and how much my mental health has improved.",
    author: "N.H.",
    location: "Verified client",
  },
  {
    quote:
      "Ffyona was absolutely amazing and navigated me through some really difficult times. Ffyona is kind, compassionate and created a really comforting and safe space for me to talk about my experiences.",
    author: "D.J.",
    location: "Verified client",
  },
  {
    quote:
      "Ffyona has helped me in more ways than I could count. We have worked on building healthy relationships and the importance of boundary work. So lovely to have a safe space to share experiences and process negative thoughts. Would recommend to everyone.",
    author: "C.J.",
    location: "Verified client",
  },
  {
    quote:
      "I didn't realise I was suffering from depression until I realised I probably shouldn't still be crying over the passing of my sister, which was very sudden. Finding Ffyona was the best thing that ever happened to me. She is very understanding and easy to speak openly with. There is never any pressure, and after every session I really did feel I was getting stronger and stronger. Thank you Ffyona for all your help.",
    author: "Y.T.",
    location: "Verified client",
  },
  {
    quote:
      "If you are looking for a counsellor who is passionate about her work, then Ffyona is the counsellor for you. She is warm, kind and friendly, and this promotes an excellent therapeutic relationship where you can share whatever you wish without feeling judged. She really listens to not only what you say but also what you don't. She walks alongside you and empowers you to make your own choices so that life becomes so much better.",
    author: "S.A.",
    location: "Verified client",
  },
  {
    quote:
      "I cannot recommend Ffyona enough. I came to her at a time where I was struggling to keep myself calm in my day-to-day life and she offered me so much support. I have learnt so much about myself and where my intense emotions stem from. One of my favourite things about Ffyona is that she won't let me avoid difficult topics — she does it with empathy and grace. Ffyona has opened up a new world to me.",
    author: "N.S.",
    location: "Verified client",
  },
  {
    quote:
      "I came to Ffyona with low self-confidence and a feeling of something lacking in my life. Through her compassion and knowledge she helped me find a version of me that I love. She listened in a non-judgemental way whilst asking challenging questions, and gave me the space to work out who I am. I feel lighter, I feel like me. It's been completely life changing. Thank you, Ffyona — I'm extremely grateful I reached out and found you.",
    author: "L.W.",
    location: "Verified client",
  },
  {
    quote:
      "Ffyona is an excellent therapist. I would highly recommend her to anyone seeking therapy. She has the ability to create a warm, safe and empathetic environment in any space. 10 out of 10.",
    author: "T.D.",
    location: "Verified client",
  },
  {
    quote:
      "Ffyona helped me with my confidence and anxieties — she was brilliant. I can now face my confidence issues and when it gets too much I put myself in my happy place. Thank you.",
    author: "H.F.",
    location: "Verified client",
  },
];

export const FAQS = [
  {
    q: "What happens in the first session?",
    a: "The first session is a gentle, unhurried conversation. We'll talk about what has brought you here, what you'd like to feel differently, and how we might work together. There's no pressure to share more than you're ready to.",
  },
  {
    q: "How many sessions will I need?",
    a: "There's no fixed number — everyone is different. Some people come for a few sessions, others for longer. We review together as we go, and there's never any obligation to continue.",
  },
  {
    q: "Is counselling confidential?",
    a: "Yes. What you share stays between us, within professional and ethical boundaries which we'll talk through together right at the start so you feel completely comfortable.",
  },
  {
    q: "Can I have online sessions?",
    a: "Absolutely. Sessions are available in person in Swindon, over Zoom, or by phone — whatever feels most comfortable and convenient for you.",
  },
  {
    q: "How do I arrange appointments?",
    a: "Simply send a message through the enquiry form, email me or call. I'll personally respond within one working day to arrange a suitable time. I can offer a range of days and times.",
  },
  {
    q: "What if I'm nervous?",
    a: "Feeling nervous is completely natural — most people do. There's no right way to begin. We'll go entirely at your pace, and the free consultation is a lovely, low-pressure way to see how you feel.",
  },
  {
    q: "How much does it cost?",
    a: "The initial counselling consultation is £45 (including a questionnaire, goal setting and contracting), and ongoing counselling sessions are £50 for one hour. We also begin with a free 30-minute consultation.",
  },
  {
    q: "Can I stop whenever I want?",
    a: "Always. You're in control throughout. There's no lock-in and no obligation — you can pause or finish counselling whenever feels right for you.",
  },
];

export const MEMBERSHIPS = [
  "MNCPS Accredited",
  "Cruse Bereavement Care Trained",
  "Level 5 Qualified Counsellor",
  "Qualified Clinical Supervisor",
];
